import { useCallback, useMemo, useRef } from 'react'
import { useModalContext } from './ModalContext'

/**
 * Hook to control a specific modal type
 *
 * @param {string} modalType - The modal type from MODALS enum
 * @returns {Object} Modal controls { isOpen, props, show, hide, update, confirm, cancel }
 *
 * @example
 * const deleteModal = useModal(MODALS.COMMON.DELETE)
 *
 * const handleDelete = async () => {
 *   const confirmed = await deleteModal.show({
 *     title: 'Delete Item',
 *     message: 'Are you sure?'
 *   })
 *   if (confirmed) {
 *     // perform delete
 *   }
 * }
 */
export function useModal(modalType) {
    const { modals, show, hide, update } = useModalContext()

    // Find modal instance by type (for isOpen and props)
    const modalInstance = useMemo(() => {
        const entry = Object.entries(modals).find(
            ([, modal]) => modal.modalType === modalType && modal.isOpen
        )
        return entry ? { instanceId: entry[0], ...entry[1] } : null
    }, [modals, modalType])

    const isOpen = Boolean(modalInstance?.isOpen)
    const props = modalInstance?.props || {}

    // Use ref to track current instanceId to avoid stale closures
    // Update synchronously during render to avoid timing issues
    const instanceIdRef = useRef(null)
    if (modalInstance?.instanceId !== instanceIdRef.current) {
        instanceIdRef.current = modalInstance?.instanceId || null
    }

    const showModal = useCallback(
        async (modalProps = {}) => {
            const result = await show(modalType, modalProps)
            return result
        },
        [show, modalType]
    )

    const hideModal = useCallback(
        (result) => {
            // Use ref to get current instanceId (avoids stale closure)
            const currentInstanceId = instanceIdRef.current
            if (currentInstanceId) {
                hide(currentInstanceId, result)
            }
        },
        [hide]
    )

    const updateModal = useCallback(
        (newProps) => {
            if (modalInstance?.instanceId) {
                update(modalInstance.instanceId, newProps)
            }
        },
        [update, modalInstance?.instanceId]
    )

    return useMemo(
        () => ({
            isOpen,
            props,
            show: showModal,
            hide: hideModal,
            update: updateModal,
            // Convenience methods
            confirm: () => hideModal(true),
            cancel: () => hideModal(false),
        }),
        [isOpen, props, showModal, hideModal, updateModal]
    )
}

/**
 * Hook to show any modal imperatively
 *
 * @returns {Object} Modal manager { showModal, hideModal, isModalOpen, getOpenModals }
 *
 * @example
 * const { showModal } = useModalManager()
 * await showModal(MODALS.COMMON.DELETE, { title: 'Delete?' })
 */
export function useModalManager() {
    const { show, hide, modals } = useModalContext()

    return {
        showModal: show,
        hideModal: hide,
        isModalOpen: (modalType) =>
            Object.values(modals).some((m) => m.modalType === modalType && m.isOpen),
        getOpenModals: () =>
            Object.entries(modals)
                .filter(([, m]) => m.isOpen)
                .map(([id, m]) => ({ instanceId: id, ...m })),
    }
}
